import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contaApi } from '../../services/api';
import { formatarMoeda, formatarData } from '../../lib/format';
import { BadgeStatusLoja, BadgePagamentoLoja, podePagarOnline, etapasPedido, indiceEtapa } from './statusLoja';
import { ChevronDown, CreditCard, QrCode, Receipt, Check, CalendarClock } from 'lucide-react';

const rotulosEtapa: Record<string, string> = {
  AGUARDANDO_PAGAMENTO: 'Pagamento',
  PAGO: 'Confirmado',
  EM_PRODUCAO: 'Em produção',
  PRONTO: 'Pronto',
  ENTREGUE: 'Entregue',
};

export function BotaoPagar({ pedidoId, valor }: { pedidoId: string; valor: number }) {
  const [erro, setErro] = useState('');
  const pagar = useMutation({
    mutationFn: () => contaApi.pagarPedido(pedidoId),
    onSuccess: (res) => {
      const url = res.data.checkout?.initPoint || res.data.checkout?.sandboxInitPoint;
      if (url) {
        window.location.href = url;
      } else {
        setErro('Não foi possível iniciar o pagamento online.');
      }
    },
    onError: (err: any) => {
      setErro(err.response?.data?.error || 'Falha ao iniciar o pagamento.');
    },
  });

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        onClick={() => { setErro(''); pagar.mutate(); }}
        disabled={pagar.isPending}
        className="btn-primary !py-2 !px-4 !text-[13px]"
      >
        <CreditCard className="w-4 h-4" />
        {pagar.isPending ? 'Abrindo checkout...' : `Pagar ${formatarMoeda(valor)}`}
      </button>
      {erro && <span className="text-xs font-medium text-red-600 max-w-[220px]">{erro}</span>}
    </span>
  );
}

function LinhaDoTempo({ status }: { status: string }) {
  if (status === 'CANCELADO') return null;
  const atual = indiceEtapa(status);
  return (
    <div className="flex items-center gap-0 mt-4">
      {etapasPedido.map((etapa, i) => {
        const concluida = i <= atual;
        return (
          <div key={etapa} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  concluida ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-400'
                }`}
              >
                {concluida ? <Check className="w-3.5 h-3.5" /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
              </div>
              <span className={`text-[10px] font-semibold whitespace-nowrap ${concluida ? 'text-brand-700' : 'text-ink-400'}`}>
                {rotulosEtapa[etapa]}
              </span>
            </div>
            {i < etapasPedido.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1.5 mb-5 rounded ${i < atual ? 'bg-brand-600' : 'bg-ink-100'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CartaoPedido({ pedido }: { pedido: any }) {
  const [aberto, setAberto] = useState(false);
  const queryClient = useQueryClient();

  return (
    <div className="card !p-0 overflow-hidden">
      <button
        onClick={() => {
          setAberto(!aberto);
          if (!aberto) queryClient.invalidateQueries({ queryKey: ['conta-pedidos'] });
        }}
        className="w-full flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4 text-left hover:bg-ink-50/60 transition-colors"
      >
        <div className="min-w-0">
          <p className="text-sm font-bold text-ink-950">Pedido #{pedido.numero}</p>
          <p className="text-xs text-ink-400 mt-0.5">{formatarData(pedido.criadoEm)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BadgeStatusLoja status={pedido.status} />
          <BadgePagamentoLoja status={pedido.pagamentoStatus} />
        </div>
        <div className="ml-auto flex items-center gap-4">
          <p className="font-display text-base font-bold text-ink-950">{formatarMoeda(pedido.valorTotal)}</p>
          <ChevronDown className={`w-4 h-4 text-ink-400 transition-transform ${aberto ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {aberto && (
        <div className="border-t border-ink-100 px-5 py-5 animate-fade-in">
          <LinhaDoTempo status={pedido.status} />

          {/* Previsão de entrega (vem do pedido de produção) */}
          {pedido.pedidoProducao?.prazo && !['ENTREGUE', 'CANCELADO'].includes(pedido.status) && (
            <p className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-600 bg-brand-50/70 rounded-lg px-3.5 py-2">
              <CalendarClock className="w-4 h-4 text-brand-600" />
              Previsão de entrega: <strong className="text-ink-800">{formatarData(pedido.pedidoProducao.prazo)}</strong>
            </p>
          )}

          {/* Itens */}
          <div className="mt-5 space-y-2.5">
            {pedido.itens.map((item: any) => (
              <div key={item.id} className="flex items-center justify-between gap-4 text-sm">
                <p className="text-ink-700">
                  <span className="font-semibold">{item.quantidade}× {item.produtoNome}</span>
                  <span className="text-ink-400"> · {item.cor} · {item.tamanho}{item.estampaNome ? ` · ${item.estampaNome}` : ' · Lisa'}</span>
                </p>
                <p className="font-semibold text-ink-900 whitespace-nowrap">{formatarMoeda(item.valorTotal)}</p>
              </div>
            ))}
          </div>

          {pedido.observacoes && (
            <p className="mt-4 text-[13px] text-ink-500 bg-ink-50 rounded-lg px-3.5 py-2.5">
              <span className="font-semibold text-ink-600">Observações: </span>{pedido.observacoes}
            </p>
          )}

          {/* Rodapé: pagamento */}
          <div className="mt-5 pt-4 border-t border-ink-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[13px] text-ink-500">
              {pedido.pagamentoMetodo === 'PIX' ? <QrCode className="w-4 h-4" /> : <Receipt className="w-4 h-4" />}
              {pedido.pagamentoMetodo
                ? `Forma escolhida: ${pedido.pagamentoMetodo === 'PIX' ? 'PIX' : 'Cartão de crédito'}`
                : 'Forma de pagamento a combinar'}
            </div>
            {podePagarOnline(pedido) && <BotaoPagar pedidoId={pedido.id} valor={pedido.valorTotal} />}
          </div>
        </div>
      )}
    </div>
  );
}
